import React, { useEffect, useState } from 'react';
import { ArrowDownIcon, FilterIcon } from '../assets/icons';
import FilterBadge from '../components/common/FilterBadge';
import PersonelModal from '../components/PersonelModal';
import { getPersonelData, getPersonelExcelData } from '../services/personelService';
import Pagination from '../components/Pagination';
import { getAllUsers } from '../services/userService';
import { getDistricts } from '../services/addressService';
import PersonelFilterModal from '../components/PersonelFilterModal';
import { formatNumber } from '../helpers/stringHelpers';
import { Dropdown } from 'primereact/dropdown';


const TIME_PERIODS = [
    { label: 'Tümü', value: '' },
    { label: 'Bugün', value: 0 },
    { label: '1 Hafta', value: 7 },
    { label: '1 Ay', value: 30 },
    { label: '1 Yıl', value: 365 }
];

const Personel = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [persons, setPersons] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDetailUser, setSelectedDetailUser] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedDays, setSelectedDays] = useState(0);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchDistricts();
  }, []);

  useEffect(() => {
    fetchPersonelData();
  }, [currentPage, selectedDays, appliedFilters]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllUsers(appliedFilters);
      setUsers(data); // TODO uncomment with correct endpoint
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Kullanıcı listesi yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await getDistricts();
      const data = await response;
      if (data.status === 200) {
        setDistricts(data.data || []);
      } else {
        setDistricts([]);
        setError('İlçeler yüklenirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Failed to fetch districts:', error);
      setDistricts([]);
      setError('İlçeler yüklenirken bir hata oluştu.');
    }
  };

  const fetchPersonelData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Format user IDs
      const userids = appliedFilters.person 
        ? appliedFilters.person.map(person => person).join('#')
        : undefined;

      // Format streets
      const sokak = appliedFilters.street 
        ? appliedFilters.street.join('#')
        : '';

      // Get district and neighborhood names
      const ilce = appliedFilters.district?.label || '';
      const mah = appliedFilters.neighborhood?.label || '';

      // Get date in YYYY-MM-DD format
      const date = appliedFilters.date || '';

      const data = await getPersonelData({
        offset: currentPage,
        days: selectedDays,
        userids,
        ilce,
        mah,
        sokak,
        date
      });

      setPersons(data);
      setTotalPages(Math.ceil(data.totalRecords / 15));
    } catch (error) {
      console.error('Failed to fetch personel data:', error);
      setError('Personel verileri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExcelDownload = async () => {
    try {
      // Format user IDs
      const userids = appliedFilters.person 
        ? appliedFilters.person.map(person => person).join('#')
        : undefined;
      // Format streets
      const sokak = appliedFilters.street 
        ? appliedFilters.street.join('#')
        : '';
      // Get district and neighborhood names
      const ilce = appliedFilters.district?.label || '';
      const mah = appliedFilters.neighborhood?.label || '';
      // Get date in YYYY-MM-DD format
      const date = appliedFilters.date || '';
      
      const response = await getPersonelExcelData({
        offset: currentPage,
        days: selectedDays,
        userids,
        ilce,
        mah,
        sokak,
        date
      });

      // Create a blob from the response
      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set the filename
      const today = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
      link.download = `personel-verileri-${today}.xlsx`;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to fetch excel data:', error);
      setError('Excel verileri yüklenirken bir hata oluştu.');
    }
  };

  const handlePeriodChange = (e) => {
    setSelectedDays((e.target.value && !isNaN(e.target.value)) ? Number(e.target.value) : '');
    setCurrentPage(0);
  };

  const handleFilterChange = (filters) => {
    setAppliedFilters(filters);
    setCurrentPage(0);
  };

  const handleRemoveFilter = (filterKey) => {
    const updatedFilters = { ...appliedFilters };
    delete updatedFilters[filterKey];
    setAppliedFilters(updatedFilters);
    setCurrentPage(0);
  };

  const handleRemoveSpecificFilter = (specificFilterKey) => {
    const { filterKey, valueIndex } = specificFilterKey;
    const currentValues = [...appliedFilters[filterKey]];
    currentValues.splice(valueIndex, 1);
    
    const updatedFilters = { ...appliedFilters };
    if (currentValues.length > 0) {
      updatedFilters[filterKey] = currentValues;
    } else {
      delete updatedFilters[filterKey];
    }
    
    setAppliedFilters(updatedFilters);
    setCurrentPage(0);
  };

  const handleRowClick = (user, event) => {
    if (
      event.target.closest('.badge') || 
      event.target.closest('a') || 
      event.target.closest('button')
    ) {
      return;
    }
    setSelectedDetailUser(user);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setTimeout(() => setSelectedDetailUser(null), 300);
  };

  if (isLoading) {
    return (
      <div className="people-container">
        <h1 className="page-title">Personel Verileri</h1>
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="people-container">
        <h1 className="page-title">Personel Verileri</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="people-container">
      <h1 className="page-title">Personel Verileri</h1>

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="filter-container">
          <button className="download-excel-button" onClick={handleExcelDownload}>
            <p>Excel Çıktısı Al</p>
            <ArrowDownIcon color='white' className="filter-icon" />
          </button>
          <button className="general-filter-button" onClick={() => setFilterModalOpen(true)}>
            <span>Geniş Filtreleme</span>
            <FilterIcon color='#18964e' className="filter-icon" />
          </button>
          <Dropdown value={selectedDays} onChange={handlePeriodChange} options={TIME_PERIODS} placeholder='Tarih Seçiniz' className='time-select personel-time-select' />
        </div>
      </div>

      {/* Applied Filters Display */}
      {Object.keys(appliedFilters).length > 0 && (
        <div className="applied-filters-container">
          {Object.entries(appliedFilters).map(([key, value]) => {
            if (key === 'date' && value) {
              return (
                <FilterBadge 
                  key={key}
                  filterKey={key}
                  filterValue={new Date(value).toLocaleDateString('tr-TR')}
                  filterLabel="Tarih"
                  onRemove={handleRemoveFilter}
                />
              );
            }
            
            if (Array.isArray(value) && value.length > 0) {
              return value.map((item, index) => {
                if (!item) return null;
                
                // For person filter, find the user's name
                if (key === 'person') {
                  const user = users.find(u => u.id === item);
                  if (!user) return null;
                  return (
                    <FilterBadge 
                      key={`${key}_${index}`}
                      filterKey={{ filterKey: key, valueIndex: index }}
                      filterValue={`${user.name} ${user.surname}`}
                      filterLabel="Personel"
                      onRemove={handleRemoveSpecificFilter}
                    />
                  );
                }
                
                // For other array filters
                const displayValue = typeof item === 'object' && item.label ? item.label : item;
                return (
                  <FilterBadge 
                    key={`${key}_${index}`}
                    filterKey={{ filterKey: key, valueIndex: index }}
                    filterValue={displayValue}
                    filterLabel={key === 'street' ? 'Sokak' : ''}
                    onRemove={handleRemoveSpecificFilter}
                  />
                );
              });
            }
            
            if (value && typeof value === 'object' && !Array.isArray(value) && value.label) {
              return (
                <FilterBadge 
                  key={key}
                  filterKey={key}
                  filterValue={value.label}
                  filterLabel={key === 'district' ? 'İlçe' : 
                             key === 'neighborhood' ? 'Mahalle' : 
                             key === 'description' ? 'Açıklama' : ''}
                  onRemove={handleRemoveFilter}
                />
              );
            }
            
            return null;
          })}
        </div>
      )}

      {/* Statistics Section */}
      {persons.statistic && (
        <div className="statistics-section">
          <div className="stat-card-container separated-waste-statistics">
            <div className="stat-card">
              <h3>Toplam Alandaki<br/>Personel Sayısı</h3>
              <p className='stat-card-number'>{formatNumber(persons.statistic.totalPersonel)}</p>
              <p className='stat-card-text'>kişi</p>
            </div>
            <div className="stat-card green">
              <h3>Toplam<br/>Verilen Poşet Sayısı</h3>
              <p className='stat-card-number'>{formatNumber(persons.statistic.verilenToplamPoset)}</p>
              <p className='stat-card-text'>adet poşet</p>
            </div>
            <div className="stat-card">
              <h3>Toplam<br/>Alınan Atık Sayısı</h3>
              <p className='stat-card-number'>{formatNumber(persons.statistic.toplamAtik)}</p>
              <p className='stat-card-text'>adet poşet</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="table-container">
        <table className="personel-table">
          <thead>
            <tr>
              <th>Tarih ve Saat</th>
              <th>Personel Adı ve Soyadı</th>
              <th>İlçe</th>
              <th>Mahalle</th>
              <th>Sokak</th>
              <th>Apartman Adı</th>
              <th>Verilen Poşet</th>
              <th>Alınan Atık</th>
              <th>Açıklama</th>
            </tr>
          </thead>
          <tbody>
            {persons?.data?.map((user) => (
              <tr 
                key={user.id} 
                onClick={(e) => handleRowClick(user, e)}
                className="clickable-row"
              >
                <td>{new Date(user.records[0].dateGiven).toLocaleString('tr-TR')}</td>
                <td>{user.records[0].personelAlinan}</td>
                <td><span className="badge district">{user.records[0].ilce}</span></td>
                <td>{user.records[0].mahalle}</td>
                <td>{user.records[0].sokak}</td>
                <td><span className="apartment-name">{user.records[0].apartman}</span></td>
                <td>{user.verilenPosetSayisi > 0 ? `${user.verilenPosetSayisi} poşet` : '-'}</td>
                <td>{user.alinanPosetSayisi > 0 ? `${user.alinanPosetSayisi} atık` : '-'}</td>
                <td>
                  {(() => {
                    switch(user.description) {
                      case 'Projeye Katılmıyor':
                        return <span className="badge red">Projeye Katılmıyor</span>;
                      case 'Adres Yanlış/Hatalı/Eksik':
                        return <span className="badge yellow">Adres Yanlış/Hatalı/Eksik</span>;
                      case 'Açıklamayı Gör':
                        return <span className="badge grey">Açıklamayı Gör</span>;
                      default:
                        return '-';
                    }
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Detail Modal */}
      <PersonelModal
        isOpen={detailModalOpen}
        onClose={handleCloseDetailModal}
        userData={selectedDetailUser}
      />
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
      <PersonelFilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onFilterChange={handleFilterChange}
        currentFilters={appliedFilters}
      />
    </div>
  );
};

export default Personel;
